/* eslint-disable no-unused-vars */
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Menu,
  MenuItem,
  MenuProps,
  TextField,
  Typography,
  useMediaQuery,
  withStyles
} from '@material-ui/core';
import {
  Add,
  AddToQueue,
  Beenhere,
  CameraAlt,
  Clear,
  PersonAdd,
  RotateLeft,
  SaveAltOutlined
} from '@material-ui/icons';
import PrintIcon from '@material-ui/icons/Print';
// import { Autocomplete } from '@material-ui/lab';
import React, {
  FC,
  memo,
  useCallback,
  useEffect,
  useState,
  KeyboardEvent
} from 'react';
import { SwitchLabel } from 'src/components';
import { SplitButtons } from 'src/components/buttons/SplitButtons';
import { theme } from 'src/theme';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import useQuotation from 'src/hooks/quotation-v2/use-quotation';
import { QuotationLinkDialog } from './dialog/QuotationLinkDialog';
import useQuotationProduct from 'src/hooks/quotation-v2/use-quotation-products';
import useQuotationApi from 'src/hooks/quotation-v2/use-quotation-api';
import TemplateForm from './table/TemplateForm';
import { NewTemplateDetails, Quotation, TemplateDetails } from 'src/types';
import { useSnackBar } from 'src/hooks';
import { srpOptions } from 'src/constants';
import { SRPOption } from 'src/types/srp-options';
import {
  isQuotationPCBuild,
  quotationProductTemplateFormater,
  srpPercentageToNumber,
  timeStampNow
} from 'src/utils';
import { useLocation, useNavigate } from 'react-router';
import AddCircleIcon from '@material-ui/icons/AddCircle';

interface QuotationV2FootbarProps {
  onCapture: () => void;
  openPrintSlipModal: () => void;
}

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5',
    marginTop: 3
  }
})((props: MenuProps) => (
  <Menu
    elevation={0}
    getContentAnchorEl={null}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center'
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    color: theme.palette.text.primary
  }
}))(MenuItem);

const useStyles = makeStyles(() => ({
  buttonContainer: {
    margin: theme.spacing(1.5)
  },
  switchContainer: {
    margin: theme.spacing(0)
  }
}));

const Component: FC<QuotationV2FootbarProps> = ({
  onCapture,
  openPrintSlipModal
}) => {
  const classes = useStyles();
  const snackBar = useSnackBar();
  const navigate = useNavigate();
  const location = useLocation();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const {
    openDialog,
    setOpenDialog,
    walkinUriQR,
    walkInFullLink,
    ecommLink,
    selectedVoucherCode,
    setSelectedVoucherCode,

    onGenerateLink
  } = useQuotation();
  const {
    quotationData,
    quotationParams,
    templateDetails,
    onAddStockRow,
    onAddCustomRow,
    removeEmptyRows,
    resetQuotation,
    setQuotationParams
  } = useQuotationProduct();
  const {
    createTemplate,
    updateTemplate,
    checkQuotationAvailability,
    checkQuotationItemsAvailability,
    validateVoucher,
    totalAmount
  } = useQuotationApi();

  const hasVoucherOnQuotationTable = quotationData.some(
    (item) => item.product?.category_name === 'VOUCHER'
  );

  const [
    anchorElQuotation,
    setAnchorElQuotation
  ] = React.useState<null | HTMLElement>(null);
  const [
    anchorElTemplate,
    setAnchorElTemplate
  ] = React.useState<null | HTMLElement>(null);

  const [isOpenTemplate, setIsOpenTemplate] = useState<boolean>(false);
  const [srpValue, setSrpValue] = useState<SRPOption>(srpOptions.promoPrice);
  const [createCustomerLoading, setCreateCustomerLoading] = useState<boolean>(
    false
  );

  const canValidateVoucher =
    Boolean(selectedVoucherCode) &&
    quotationData.some(
      (item) =>
        item.product?.product_id &&
        item.product?.category_id !== 35 &&
        item.product?.category_id !== 52
    ) &&
    totalAmount > 0;

  const handleClickQuotation = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElQuotation(event.currentTarget);
  };
  const handleClickTemplate = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElTemplate(event.currentTarget);
  };

  const handleCloseQuotation = () => {
    setAnchorElQuotation(null);
  };

  const handleCloseTemplate = () => {
    setAnchorElTemplate(null);
  };

  const onHandleSaveTemplate = useCallback(
    async (action: string, name: string, template_id?: number) => {
      const cleanQuotationData = quotationData?.filter(
        (item) =>
          item?.product?.product_name !== undefined &&
          item?.product?.product_name !== ''
      );

      const checkerRes = await checkQuotationAvailability({
        items: quotationData,
        branch_id: quotationParams?.branch_id
      });

      const hasWarningNotes = checkerRes?.originalData?.quote_items?.some(
        (item) => !!item?.warning_note
      );

      if (checkerRes?.success) {
        if (hasWarningNotes) {
          snackBar.show({
            severity: 'error',
            message:
              'Some item in quotation is invalid or has warning kindly check'
          });
          return;
        }
        const finalTemplate: NewTemplateDetails = {
          products: cleanQuotationData,
          name: name,
          pc_build: quotationParams?.pc_build,
          boost: quotationParams?.boost,
          branch_id: quotationParams?.branch_id || 1
        };
        if (action === 'create') {
          createTemplate(finalTemplate);
          return;
        }

        if (action === 'update') {
          if (!template_id) {
            snackBar.show({
              severity: 'warning',
              message: 'Please select template first to update'
            });
            return;
          }

          updateTemplate({ id: template_id, ...finalTemplate });
          return;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quotationData, quotationParams]
  );

  const handleSwitchChange = (key: 'pc_build' | 'boost', value: boolean) => {
    setQuotationParams({
      ...quotationParams,
      [key]: value ? 1 : 0
    });
  };

  const onHandleApplySrp = (srp: SRPOption, quotationData: Quotation[]) => {
    const srpNum = srpPercentageToNumber(srp.value);
    //register to state holder params

    snackBar?.show({
      severity: 'success',
      message: !quotationParams?.is_srp ? 'SRP applied' : 'Promo Price Applied'
    });
    setQuotationParams({
      ...quotationParams,
      currentSrpValue: srp,
      is_srp: !quotationParams?.is_srp
    });
  };

  const onAddCustomer = async (branch_id: number, boost: number) => {
    setCreateCustomerLoading(true);
    const quoteItems = await checkQuotationAvailability({
      items: quotationData,
      branch_id: quotationParams?.branch_id
    });

    const newQuoteItems = quoteItems?.originalData?.quote_items;

    if (!newQuoteItems || newQuoteItems.length <= 0) {
      snackBar.show({
        severity: 'error',
        message: 'Error structuring quotation and checking of availability',
        useSound: true
      });
      setCreateCustomerLoading(false);
      return;
    }

    const hasWarning = newQuoteItems?.find((x) => x.warning_note);
    if (hasWarning) {
      snackBar.show({
        severity: 'error',
        message:
          'Some item(s) might be out of stock. Please check availability.',
        useSound: true
      });
      setCreateCustomerLoading(false);
      return;
    }

    const newTemplateProducts = quotationProductTemplateFormater(newQuoteItems);
    const isNewTemplatePCBuild = isQuotationPCBuild(newTemplateProducts);
    const finalNewTemplate: NewTemplateDetails = {
      products: newTemplateProducts,
      name: `AddCustomer-${timeStampNow()}`,
      pc_build: isNewTemplatePCBuild ? 1 : 0,
      boost: boost,
      branch_id: branch_id
    };

    // Finally save new template
    const createQuoteResponse = await createTemplate(finalNewTemplate);

    const quotationTemplateId =
      createQuoteResponse?.originalData?.data?.quotation_id;

    if (quotationTemplateId) {
      snackBar.show({
        severity: 'success',
        message: 'Template created successfully. Now you can add customer',
        useSound: true
      });

      setCreateCustomerLoading(false);
      const salesTransferTicketId = new URLSearchParams(location.search).get(
        'sales_transfer_ticket_id'
      );
      const salesTransferTicketParam = salesTransferTicketId
        ? `&sales_transfer_ticket_id=${salesTransferTicketId}`
        : '';

      navigate(
        `/app/customer-taptap/add?internal_quotation_id=${quotationTemplateId}${salesTransferTicketParam}`
      );
      return;
    }

    snackBar.show({
      severity: 'error',
      message: 'Error creating template',
      useSound: true
    });
    setCreateCustomerLoading(false);
    // createTemplate({})
    // TODO: Generate Template ID.
  };

  const onHandleValidateVoucher = async () => {
    if (!canValidateVoucher) return;

    const validated = await validateVoucher(
      selectedVoucherCode,
      totalAmount,
      quotationData
    );

    if (validated?.success) {
      onAddCustomRow(
        [
          ...quotationData,
          {
            custom: false,
            quantity: 1,
            product: {
              category_name: 'VOUCHER',
              product_name: selectedVoucherCode,
              retail_price: -(
                validated?.originalData?.data?.total_discounted_price || 0
              )
            }
          }
        ],
        true
      );
      setSelectedVoucherCode('');
      snackBar.show({
        severity: 'success',
        message: 'Voucher applied successfully'
      });
    }
  };

  const handleKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      await onHandleValidateVoucher();
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1em'
      }}
    >
      <Card
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '1em',
          padding: '1em'
        }}
      >
        <Grid container spacing={2}>
          {/* TODO: will remove later or improve next sprint */}
          {/* <Grid item xs={12} sm={3}>
            <SwitchLabel
              className={classes.switchContainer}
              label="Allow Compatibility"
              onChange={() => {
                'test';
              }}
            />
          </Grid> */}

          {/* <Grid item xs={12} sm={3}>
            <SwitchLabel
              className={classes.switchContainer}
              label="Show clean SRP Quote"
              onChange={() => {
                'test';
              }}
            />
          </Grid> */}

          <Grid item xs={12} sm={3}>
            <SwitchLabel
              className={classes.switchContainer}
              label="PC Build"
              checked={!!quotationParams?.pc_build}
              onChange={(value) => handleSwitchChange('pc_build', value)}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <SwitchLabel
              className={classes.switchContainer}
              label="Boost"
              checked={!!quotationParams?.boost}
              onChange={(value) => handleSwitchChange('boost', value)}
            />
          </Grid>
        </Grid>
      </Card>

      <Card
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '1em',
          padding: '1em'
        }}
      >
        <Grid container>
          <Grid item xs={12} sm={7}>
            <Box
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}
            >
              <Button
                variant="outlined"
                startIcon={<Add color="primary" />}
                className={classes.buttonContainer}
                onClick={() => onAddStockRow(quotationData)}
              >
                {`Add "In-Stock" Row`}
              </Button>

              <Button
                variant="outlined"
                startIcon={<AddToQueue color="primary" />}
                className={classes.buttonContainer}
                onClick={() => onAddCustomRow(quotationData)}
              >
                {`Add "Custom / Pre-Order" Row`}
              </Button>

              <Button
                variant="outlined"
                startIcon={<Beenhere color="primary" />}
                className={classes.buttonContainer}
                onClick={() =>
                  checkQuotationItemsAvailability({
                    items: quotationData,
                    branch_id: quotationParams?.branch_id
                  })
                }
              >
                Check Availability
              </Button>

              <Button
                variant="outlined"
                startIcon={<RotateLeft color="secondary" />}
                className={classes.buttonContainer}
                onClick={() => resetQuotation()}
              >
                <Typography color="secondary"> Reset to New</Typography>
              </Button>

              <Button
                variant="outlined"
                startIcon={<Clear color="secondary" />}
                className={classes.buttonContainer}
                onClick={() => removeEmptyRows(quotationData)}
              >
                <Typography color="secondary"> Remove Empty Fields</Typography>
              </Button>

              <Button
                variant="outlined"
                startIcon={<SaveAltOutlined color="primary" />}
                className={classes.buttonContainer}
                onClick={() => setIsOpenTemplate(true)}
              >
                Save Template
              </Button>

              <Button
                variant="outlined"
                startIcon={<CameraAlt color="primary" />}
                className={classes.buttonContainer}
                onClick={() => onCapture()}
              >
                Screenshot (Copy)
              </Button>

              <Button
                variant="outlined"
                startIcon={<PrintIcon color="primary" />}
                className={classes.buttonContainer}
                onClick={() => openPrintSlipModal()}
              >
                Print Quotation Slip
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sm={5}>
            <Grid
              container
              spacing={2}
              alignItems="flex-end"
              justifyContent={isSmUp ? 'flex-end' : 'flex-start'}
              direction="column"
            >
              <Grid item style={{ justifySelf: 'flex-end' }}>
                <div className={classes.buttonContainer}>
                  <SplitButtons
                    title="Current Pricing"
                    options={Object.values(srpOptions)}
                    disableButton={
                      quotationParams?.currentSrpValue?.name === srpValue.name
                    }
                    onChange={(option: SRPOption) => {
                      setSrpValue(option);
                    }}
                    onApplyClick={() =>
                      onHandleApplySrp(srpValue, quotationData)
                    }
                  />
                </div>
              </Grid>
              <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                <div className={classes.buttonContainer}>
                  <TextField
                    disabled={hasVoucherOnQuotationTable}
                    placeholder="Enter Voucher Code"
                    variant="outlined"
                    fullWidth
                    label={'Enter Voucher Code'}
                    size="small"
                    value={selectedVoucherCode}
                    onChange={(e) => setSelectedVoucherCode(e.target.value)}
                    onKeyDown={handleKeyDown}
                    style={{ marginBottom: 0 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box
                            sx={{
                              height: 24,
                              mx: 1,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            <Divider orientation="vertical" flexItem />
                          </Box>
                          <IconButton
                            disabled={!canValidateVoucher}
                            onClick={() => {
                              onHandleValidateVoucher();
                            }}
                            style={{
                              color: canValidateVoucher ? 'green' : 'gray',
                              padding: '5px'
                            }}
                          >
                            <AddCircleIcon />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </div>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={7}>
            <Grid container justifyContent="flex-start">
              <Button
                className={classes.buttonContainer}
                startIcon={<Link />}
                color="primary"
                variant="contained"
                onClick={() => {
                  onGenerateLink(quotationData);
                }}
              >
                GENERATE QUOTATION LINK
              </Button>
            </Grid>
          </Grid>

          <Grid item xs={12} sm={5}>
            <Grid container justifyContent={isSmUp ? 'flex-end' : 'flex-start'}>
              <Button
                disabled={createCustomerLoading}
                className={classes.buttonContainer}
                startIcon={<PersonAdd />}
                color="primary"
                variant="contained"
                onClick={() =>
                  onAddCustomer(
                    quotationParams?.branch_id || 1,
                    quotationParams?.boost
                  )
                }
              >
                CREATE CUSTOMER
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Card>

      <QuotationLinkDialog
        qrcodeImage={walkinUriQR}
        ecommLink={ecommLink}
        link={walkInFullLink}
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
        }}
      />

      <TemplateForm
        template={templateDetails}
        isVisible={isOpenTemplate}
        handleClose={() => setIsOpenTemplate(false)}
        onProceed={(action: string, name: string) =>
          onHandleSaveTemplate(action, name, templateDetails?.id)
        }
      />
    </div>
  );
};
export const QuotationV2Footbar = memo(Component);
